import { Suspense, lazy, useEffect, useRef } from "react";
import Nav from "@/components/Nav";
import Hero from "@/components/Hero";
import CapabilityStrip from "@/components/CapabilityStrip";
import TrustedBy from "@/components/TrustedBy";
import AuditIntro from "@/components/AuditIntro";
import AuditFunnel from "@/components/AuditFunnel";
import { AnimatedTextCycle } from "@/components/AnimatedTextCycle";
import IntegrationsBand from "@/components/IntegrationsBand";
import ScrollProgress from "@/components/ScrollProgress";
import useReveal from "@/lib/useReveal";
import { useMotion, useScrollspy } from "@/lib/motion";

const NAV_SECTION_IDS = ["services", "audit", "process", "use-cases", "contact"];

const animatedAuditWords = [
  "AI Websites",
  "Voice Agents",
  "WhatsApp Business AI Bots",
  "Automation Systems",
  "Web Applications",
  "SEO + AI Visibility",
  "Vertical SaaS",
];

const deferredHomeSections = [
  () => import("@/components/Capabilities"),
  () => import("@/components/VoiceAgents"),
  () => import("@/components/TunnelSection"),
  () => import("@/components/UseCases"),
  () => import("@/components/Process"),
  () => import("@/components/FutureDeck"),
  () => import("@/components/Differentiation"),
  () => import("@/components/Pricing"),
  () => import("@/components/FAQ"),
  () => import("@/components/FinalCta"),
  () => import("@/components/Footer"),
];

const Capabilities = lazy(deferredHomeSections[0]);
const VoiceAgents = lazy(deferredHomeSections[1]);
const TunnelSection = lazy(deferredHomeSections[2]);
const UseCases = lazy(deferredHomeSections[3]);
const Process = lazy(deferredHomeSections[4]);
const FutureDeck = lazy(deferredHomeSections[5]);
const Differentiation = lazy(deferredHomeSections[6]);
const Pricing = lazy(deferredHomeSections[7]);
const FAQ = lazy(deferredHomeSections[8]);
const FinalCta = lazy(deferredHomeSections[9]);
const Footer = lazy(deferredHomeSections[10]);

export default function Home() {
  const rootRef = useRef(null);
  useReveal(rootRef);
  useMotion();
  useScrollspy(NAV_SECTION_IDS);

  useEffect(() => {
    const preloadDeferredSections = () => {
      deferredHomeSections.forEach((loadSection) => loadSection());
    };

    if ("requestIdleCallback" in window) {
      const id = window.requestIdleCallback(preloadDeferredSections);
      return () => window.cancelIdleCallback(id);
    }

    const id = window.setTimeout(preloadDeferredSections, 1200);
    return () => window.clearTimeout(id);
  }, []);

  return (
    <main ref={rootRef} data-testid="home-root">
      <ScrollProgress />
      <Nav />
      <Hero />
      <CapabilityStrip />
      <TrustedBy />
      <IntegrationsBand />
      <AuditIntro />
      <AuditFunnel />
      <section className="audit-animated-text-section">
        <p className="audit-animated-kicker">ForgeLayer identifies and builds</p>

        <h2 className="audit-animated-heading">
          <span className="audit-animated-static">Practical systems for</span>
          <AnimatedTextCycle
            words={animatedAuditWords}
            interval={2200}
            className="audit-animated-word text-[#3BA7FF]"
          />
        </h2>
      </section>
      <Suspense fallback={null}>
        <Capabilities />
        <VoiceAgents />
        <TunnelSection />
        <UseCases />
        <Process />
        <FutureDeck />
        <Differentiation />
        <Pricing />
        <FAQ />
        <FinalCta />
        <Footer />
      </Suspense>
    </main>
  );
}
